import React from 'react';
import { X, Zap, ArrowRight, Loader2 } from 'lucide-react';

export default function AccelerationConfirmModal({
    option,
    currentTier,
    userCredits,
    onConfirm,
    onCancel,
    loading
}) {
    const insufficient = userCredits < option.credits;
    const remaining = userCredits - option.credits;

    // Get current tier delivery time
    const tierHours = {
        free: 9,
        starter: 6,
        pro: 3,
        ultra: 1
    };
    const currentHours = tierHours[currentTier.toLowerCase()] || 9;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center dark:bg-slate-900/80 bg-slate-500/50 backdrop-blur-sm px-4">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl max-w-md w-full shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b dark:border-slate-800 border-slate-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl dark:bg-sky-500/20 bg-sky-100 border dark:border-sky-400/30 border-sky-200 flex items-center justify-center">
                            <Zap className="w-5 h-5 dark:text-sky-400 text-sky-600" />
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                                Accelerate Delivery
                            </p>
                            <h3 className="text-lg font-bold dark:text-white text-slate-900">{option.label}</h3>
                        </div>
                    </div>
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        className="text-slate-400 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* Time Comparison */}
                    <div className="flex items-center justify-center gap-4 py-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold dark:text-slate-400 text-slate-600">{currentHours}h</div>
                            <div className="text-xs text-slate-500 mt-1">Current</div>
                        </div>
                        <ArrowRight className="w-5 h-5 dark:text-sky-400 text-sky-600" />
                        <div className="text-center">
                            <div className="text-2xl font-bold dark:text-sky-400 text-sky-600">{option.hours}h</div>
                            <div className="text-xs text-slate-500 mt-1">Accelerated</div>
                        </div>
                    </div>

                    <div className="text-center py-2 px-4 rounded-lg dark:bg-emerald-500/10 bg-emerald-50 border dark:border-emerald-500/20 border-emerald-200">
                        <p className="text-sm dark:text-emerald-300 text-emerald-700">
                            ⏱️ Save <span className="font-bold">{option.timeSaved} hours</span>
                        </p>
                    </div>

                    <div className="border-t dark:border-slate-800 border-slate-200 pt-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm dark:text-slate-400 text-slate-500">Cost:</span>
                            <span className="text-lg font-bold dark:text-white text-slate-900">{option.credits} credits</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm dark:text-slate-400 text-slate-500">Your balance:</span>
                            <span className="text-sm font-semibold dark:text-slate-300 text-slate-800">{userCredits} credits</span>
                        </div>
                        {!insufficient && (
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500">After acceleration:</span>
                                <span className="text-slate-400">{remaining} credits</span>
                            </div>
                        )}
                    </div>

                    {insufficient && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                            <p className="text-sm text-red-300">
                                ⚠️ You need <span className="font-bold">{option.credits - userCredits}</span> more credits
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex gap-3 p-6 border-t dark:border-slate-800 border-slate-200">
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        className="flex-1 px-4 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    {insufficient ? (
                        <button
                            onClick={() => window.location.href = '/#pricing'}
                            className="flex-1 px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
                        >
                            Buy Credits
                        </button>
                    ) : (
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className="flex-1 px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Confirm - Spend {option.credits}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
